import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import { StatusBadge } from '@/components/common';
import type { ColDef } from '@/grid/types';
import type {
  OnFarmImplantApiSourceType,
  OnFarmImplantQueueRecord,
} from '@/services/on-farm-implant';

const statusColors = {
  Selected: { bg: 'bg-gray-100', text: 'text-gray-700' },
  Loaded: { bg: 'bg-blue-100', text: 'text-blue-700' },
  Queued: { bg: 'bg-green-100', text: 'text-green-700' },
  Discarded: { bg: 'bg-red-100', text: 'text-red-700' },
  Frozen: { bg: 'bg-cyan-100', text: 'text-cyan-700' },
  Unknown: { bg: 'bg-gray-100', text: 'text-gray-700' },
};

export const IMPLANT_QUEUE_SOURCE_TYPES: OnFarmImplantApiSourceType[] = [
  'fresh',
  'frozen',
  'flush',
  'client-on-farm',
  'non-tog',
];

const SOURCE_TYPE_LABEL_KEYS: Record<OnFarmImplantApiSourceType, string> = {
  fresh: 'onFarmImplant.implantQueue.sourceTypes.fresh',
  frozen: 'onFarmImplant.implantQueue.sourceTypes.frozen',
  flush: 'onFarmImplant.implantQueue.sourceTypes.flush',
  'client-on-farm': 'onFarmImplant.implantQueue.sourceTypes.clientOnFarm',
  'non-tog': 'onFarmImplant.implantQueue.sourceTypes.nonTog',
};

export const useSourceTypeLabel = () => {
  const { t } = useTranslation();

  return useMemo(
    () => (sourceType: string) => {
      const key =
        SOURCE_TYPE_LABEL_KEYS[sourceType as OnFarmImplantApiSourceType];
      return key ? t(key) : sourceType;
    },
    [t],
  );
};

export const useImplantQueueColumnDefs =
  (): ColDef<OnFarmImplantQueueRecord>[] => {
    const { t } = useTranslation();
    const sourceTypeLabel = useSourceTypeLabel();

    return useMemo(
      () => [
        {
          field: 'queueOrder',
          colId: 'queueOrder',
          headerName: t('onFarmImplant.implantQueue.queueOrder'),
          width: 90,
          minWidth: 80,
          filter: 'agNumberColumnFilter',
          sort: 'asc',
          cellClass: 'font-medium',
        },
        {
          field: 'sourceType',
          colId: 'sourceType',
          headerName: t('onFarmImplant.implantQueue.sourceType'),
          flex: 1,
          minWidth: 150,
          filter: 'agSetColumnFilter',
          valueGetter: (params) =>
            params.data ? sourceTypeLabel(params.data.sourceType) : null,
        },
        {
          field: 'embryoName',
          colId: 'embryoName',
          headerName: t('onFarmImplant.implantQueue.embryo'),
          flex: 1.4,
          minWidth: 150,
          filter: 'agTextColumnFilter',
          cellRenderer: (params: { data?: OnFarmImplantQueueRecord }) => {
            if (!params.data) return null;
            return (
              <div>
                <div className="font-medium text-gray-900">
                  {params.data.embryoName ?? '\u2014'}
                </div>
                <div className="text-xs text-gray-500">
                  {params.data.recordName ?? ''}
                </div>
              </div>
            );
          },
        },
        {
          field: 'accountName',
          colId: 'accountName',
          headerName: t('onFarmImplant.implantQueue.account'),
          flex: 1.2,
          minWidth: 130,
          filter: 'agSetColumnFilter',
        },
        {
          field: 'transferStrawNumber',
          colId: 'transferStrawNumber',
          headerName: t('onFarmImplant.implantQueue.transferStraw'),
          flex: 0.8,
          minWidth: 120,
          filter: 'agNumberColumnFilter',
        },
        {
          field: 'frozenStrawNumber',
          colId: 'frozenStrawNumber',
          headerName: t('onFarmImplant.implantQueue.frozenStraw'),
          flex: 0.8,
          minWidth: 110,
          filter: 'agNumberColumnFilter',
        },
        {
          field: 'cane',
          colId: 'cane',
          headerName: t('onFarmImplant.implantQueue.cane'),
          flex: 0.6,
          minWidth: 80,
          filter: 'agTextColumnFilter',
        },
        {
          field: 'stage',
          colId: 'stage',
          headerName: t('onFarmImplant.implantQueue.stage'),
          flex: 0.6,
          minWidth: 80,
          filter: 'agNumberColumnFilter',
        },
        {
          field: 'grade',
          colId: 'grade',
          headerName: t('onFarmImplant.implantQueue.grade'),
          flex: 0.6,
          minWidth: 80,
          filter: 'agNumberColumnFilter',
        },
        {
          field: 'freezeMethodName',
          colId: 'freezeMethodName',
          headerName: t('onFarmImplant.implantQueue.freezeMethod'),
          flex: 1,
          minWidth: 130,
          filter: 'agSetColumnFilter',
        },
        {
          field: 'embryoStatusName',
          colId: 'embryoStatusName',
          headerName: t('onFarmImplant.implantQueue.embryoStatus'),
          flex: 0.9,
          minWidth: 120,
          filter: 'agSetColumnFilter',
          cellRenderer: (params: { value?: string }) => {
            if (!params.value) return null;
            return <StatusBadge label={params.value} colorMap={statusColors} />;
          },
        },
      ],
      [sourceTypeLabel, t],
    );
  };
